import {
    EmphasizedH3Text,
    FAButton,
    MoreInfoText,
    ShowMoreInfo,
    StyledList,
} from '@/src/modules/pg-plus/PGPlusStyledComponents'
import StonlyContentModal from '@/src/common/components/marketing/modals/StonlyContentModal'
import { slideDown } from '@/src/common/animations'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faAngleDown,
    faAngleUp,
    faQuestion,
} from '@fortawesome/free-solid-svg-icons'
import { styled } from '@/src/stitches.config'
import { useState } from 'react'
import CallToActionButton from '@/src/common/components/marketing/buttons/CallToActionButton'
import { CORPORATE_BRANDS_PAGE } from '@/src/modules/pg-plus/constants'
import { TPGPlusEnabledRedemptionOptions } from '@/src/modules/build-a-card/types'

const Wrapper = styled('div', { mt: 30 })

const CapitalizedTextWrapper = styled('div', {
    display: 'flex',
    ai: 'center',
    jc: 'space-between',
    gap: '5px',
    '@media (min-width: 340px)': {
        jc: 'unset',
        gap: '20px',
    },
})

const StyledEmphasizedH3 = styled(EmphasizedH3Text, {
    width: 280,
    maxWidth: '90%',
    cursor: 'pointer',
    fontSize: '1rem',
})

interface IWaysToUseProps {
    brandName: string
    validEGiftDenoms: string
    redemptionOptions: TPGPlusEnabledRedemptionOptions
    isPgPlus: boolean
    ctaUrl?: string
    ctaText?: string
    hasPartner?: boolean
    showStonlyHelpBubble?: boolean
}

const WaysToUseWithCTA = ({
    brandName = '',
    validEGiftDenoms,
    isPgPlus,
    redemptionOptions,
    ctaText,
    ctaUrl = CORPORATE_BRANDS_PAGE,
    hasPartner = false,
    showStonlyHelpBubble = true,
}: IWaysToUseProps) => {
    const [showModal, setShowModal] = useState<boolean>(false)
    const [showRecipientWaysToUse, setShowRecipientWaysToUse] =
        useState<boolean>(true)

    const defaultText = !isPgPlus
        ? `Buy ${brandName} gift cards and hundreds of other national brands in bulk for reward or incentive opportunities!`
        : `Buy gift cards from hundreds of national brands in bulk for reward or incentive opportunities!`
    const {
        cardLinkEnabled,
        eGiftEnabled,
        changeMerchantEnabled,
        plasticEnabled,
    } = redemptionOptions

    return (
        <Wrapper>
            {!hasPartner && (
                <CallToActionButton
                    url={ctaUrl}
                    text={ctaText ?? defaultText}
                    gradientColor="gradientBorderPG"
                />
            )}
            <ShowMoreInfo
                onClick={() =>
                    setShowRecipientWaysToUse(!showRecipientWaysToUse)
                }
            >
                <MoreInfoText>More Info</MoreInfoText>
                <FontAwesomeIcon
                    icon={showRecipientWaysToUse ? faAngleUp : faAngleDown}
                />
            </ShowMoreInfo>
            {showRecipientWaysToUse && (
                <div
                    style={{
                        animation: showRecipientWaysToUse
                            ? `${slideDown} 0.5s ease-in-out`
                            : 'none',
                    }}
                >
                    {showStonlyHelpBubble && (
                        <StonlyContentModal
                            showModal={showModal}
                            setShowModal={setShowModal}
                        />
                    )}
                    <CapitalizedTextWrapper>
                        <StyledEmphasizedH3
                            data-testid={'ways-to-use'}
                            onClick={() => {
                                showStonlyHelpBubble && setShowModal(true)
                            }}
                        >
                            Ways your recipient can use their {brandName}
                            {!isPgPlus ? ' gift' : ''}:
                        </StyledEmphasizedH3>
                        {showStonlyHelpBubble && (
                            <FAButton
                                type={'button'}
                                data-testid={'fa-button'}
                                onClick={() => setShowModal(true)}
                            >
                                <FontAwesomeIcon icon={faQuestion} />
                            </FAButton>
                        )}
                    </CapitalizedTextWrapper>
                    <StyledList>
                        {cardLinkEnabled && (
                            <li>Activate to a Visa, Mastercard or Amex</li>
                        )}
                        {eGiftEnabled && (
                            <li>
                                Use as an eGift{' '}
                                {validEGiftDenoms && `(${validEGiftDenoms})`}
                            </li>
                        )}
                        {changeMerchantEnabled && (
                            <li>Swap for another merchant</li>
                        )}
                        {plasticEnabled && (
                            <li>Get a Visa gift card mailed to them</li>
                        )}
                    </StyledList>
                </div>
            )}
        </Wrapper>
    )
}

export default WaysToUseWithCTA
